import { skipToken } from '@reduxjs/toolkit/query'
import { useNavigate } from 'react-router'
import { NavLink } from 'react-router-dom'
import { useAuth } from '../../../auth/UseAuth'
import { ImgHelper } from '../../../helpers/ImgHelper'
import { useSettings } from '../../../hooks/useSettings'
import { useGetTenantQuery } from '../../../redux/api/customersApi'
import { emptyGuid, emptyString } from '../../../types/constants'
import { PermissionsEnum } from '../../../types/enums'
import { NumberPlate } from '../NumberPlate/NumberPlate'
import { UserMenuComponent } from '../UserMenu/UserMenuComponent'
import './AppHeaderComponents.scss'

interface IMenuLinkConfigItem {
  regex: RegExp
  link: string
}

interface IMenuLinkConfig {
  home: IMenuLinkConfigItem
  results: IMenuLinkConfigItem
  incoming: IMenuLinkConfigItem
  outgoing: IMenuLinkConfigItem
  upload: IMenuLinkConfigItem
  workitems: IMenuLinkConfigItem
}

const menuConfig: Readonly<IMenuLinkConfig> = {
  home: { link: 'home', regex: /\/$/ },
  results: { link: 'results', regex: /\/results\/*/ },
  incoming: { link: '/incoming', regex: /\/incoming\/*/ },
  outgoing: { link: '/outgoing', regex: /\/outgoing\/*/ },
  upload: { link: 'upload', regex: /\/upload\/*/ },
  workitems: { link: 'WorkItems', regex: /\/WorkItems\/*/ },
}

export function AppHeaderComponent() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const tenant = useGetTenantQuery(user?.tenant ?? skipToken)
  const [contractHistory, updateContractHistory] = useSettings('iLinkContractHistory', { remote: false })
  const contractHistoryKey = user?.id ?? emptyGuid
  const currentContractHistory = contractHistory[contractHistoryKey] ?? []
  const lastContractSearchResult = currentContractHistory.at(currentContractHistory.length - 1)
  const canUpload = tenant.data?.tags.some((t) => t.toLowerCase() === 'vehicleproductionupload')

  const handleClearHistoryClick = (contractId: string) => {
    const newHistory = currentContractHistory.filter((c) => c.id !== contractId)
    updateContractHistory({
      ...contractHistory,
      [contractHistoryKey]: newHistory
    })
    navigate(`/`)
  }

  const isActive = (config: IMenuLinkConfigItem) => {
    return config.regex.test(location.pathname) ? 'active' : emptyString
  }

  const logo = () => {
    const tenantTags = tenant.data?.tags ?? []

    if (tenantTags.some((t) => t.toLowerCase() === 'amag')) {
      return (
        <img
          alt="InterfleetLink"
          className="logo"
          src="/img/ilink-amag.svg"
          width="352"
          height="58"
          data-fallback="/img/ilink-amag.png"
          onError={(e) => ImgHelper.handleImageLoadingFailure(e)}
        />
      )
    } else if (tenantTags.some((t) => t.toLowerCase() === 'amagretail')) {
      return (
        <img
          alt="InterfleetLink"
          className="logo"
          src="/img/ilink-amag.svg"
          width="352"
          height="58"
          data-fallback="/img/ilink-amag.png"
          onError={(e) => ImgHelper.handleImageLoadingFailure(e)}
        />
      )
    } else if (tenantTags.some((t) => t.toLowerCase() === 'galliker')) {
      return (
        <img
          alt="InterfleetLink"
          className="logo"
          src="/img/ilink-galliker.svg"
          width="344"
          height="58"
          data-fallback="/img/ilink-galliker.png"
          onError={(e) => ImgHelper.handleImageLoadingFailure(e)}
        />
      )
    } else if (tenantTags.some((t) => t.toLowerCase() === 'renault')) {
      return (
        <img
          alt="InterfleetLink"
          className="logo"
          src="/img/ilink-renault.svg"
          width="416"
          height="58"
          data-fallback="/img/ilink-renault.png"
          onError={(e) => ImgHelper.handleImageLoadingFailure(e)}
        />
      )
    } else if (tenantTags.some((t) => t.toLowerCase() === 'sortimo')) {
      return (
        <img
          alt="InterfleetLink"
          className="logo"
          src="/img/ilink-sortimo.svg"
          width="320"
          height="62"
          data-fallback="/img/ilink-sortimo.png"
          onError={(e) => ImgHelper.handleImageLoadingFailure(e)}
        />
      )
    } else if (tenantTags.some((t) => t.toLowerCase() === 'peugeot')) {
      return (
        <img
          alt="InterfleetLink"
          className="logo"
          src="/img/ilink-peugeot.svg"
          width="304"
          height="60"
          data-fallback="/img/ilink-peugeot.png"
          onError={(e) => ImgHelper.handleImageLoadingFailure(e)}
        />
      )
    } else if (tenantTags.some((t) => t.toLowerCase() === 'concepta')) {
      return (
        <img
          alt="InterfleetLink"
          className="logo"
          src="/img/ilink-concepta.svg"
          width="427"
          height="60"
          data-fallback="/img/ilink-concepta.png"
          onError={(e) => ImgHelper.handleImageLoadingFailure(e)}
        />
      )
    } else if (tenantTags.some((t) => t.toLowerCase() === 'christinger')) {
      return (
        <img
          alt="InterfleetLink"
          className="logo"
          src="/img/ilink-christinger.svg"
          width="528"
          height="60"
          data-fallback="/img/ilink-christinger.png"
          onError={(e) => ImgHelper.handleImageLoadingFailure(e)}
        />
      )
    } else {
      return (
        <img
          alt="InterfleetLink"
          className="logo"
          src="/img/interfleetlink_logo.svg"
          width="226"
          height="50"
          data-fallback="/img/interfleetlink_logo.png"
          onError={(e) => ImgHelper.handleImageLoadingFailure(e)}
        />
      )
    }
  }

  if (!user && !lastContractSearchResult) {
    return <></>
  }

  if (location.pathname.includes('welcome')) {
    return <></>
  }

  const is4uPro = user?.is4UPro || tenant.data?.tags.some(t => t.toLowerCase() === 'interfleetpro')

  return (
    <div className="container">
      <div className="header">
        <NavLink to="/" title="InterfleetLink">
          {logo()}
        </NavLink>
        <div className="toolbar horizontal">
          <NavLink to="/" title="InterfleetLink">
            <img
              alt="InterfleetLink"
              className="linkSmall"
              src="/img/linkSmall.svg"
              width="50"
              height="50"
              data-fallback="/img/linkSmall.png"
              onError={(e) => ImgHelper.handleImageLoadingFailure(e)}
            />
          </NavLink>
          <div className="toolArea">
            <ul className="toolMenu">
              <li>
                <NavLink to="/" title="InterfleetLink">
                  <i className="icon-search"></i>
                </NavLink>
              </li>
              {lastContractSearchResult?.id ? (
                <li className={isActive(menuConfig.results)}>
                  <NavLink to={`results/${lastContractSearchResult.id}`} title="Search Results">
                    <i className="icon-result"></i>
                  </NavLink>
                </li>
              ) : null}
              {user?.isInternalUser ||
              (!is4uPro && user?.permissions?.includes(PermissionsEnum.WorkItems)) ? (
                <li className={isActive(menuConfig.workitems)}>
                  <NavLink to="/WorkItems" title="Work items">
                    <i className="icon-requests"></i>
                  </NavLink>
                </li>
              ) : null}
              {user?.isInternalUser ||
              (is4uPro && user?.permissions?.includes(PermissionsEnum.Incoming)) ? (
                <li className={isActive(menuConfig.incoming)}>
                  <NavLink to={menuConfig.incoming.link} title="InterfleetLink">
                    <i className="icon-garageIn"></i>
                  </NavLink>
                </li>
              ) : null}
              {user?.isInternalUser ||
              (is4uPro && user?.permissions?.includes(PermissionsEnum.Outgoing)) ? (
                <li className={isActive(menuConfig.outgoing)}>
                  <NavLink to={menuConfig.outgoing.link} title="InterfleetLink">
                    <i className="icon-garageOut"></i>
                  </NavLink>
                </li>
              ) : null}
              {user?.isInternalUser || canUpload ? (
                <li className={isActive(menuConfig.upload)}>
                  <NavLink to={menuConfig.upload.link} title="InterfleetLink">
                    <i className="icon-excel-import"></i>
                  </NavLink>
                </li>
              ) : null}
            </ul>
            <UserMenuComponent />
          </div>
        </div>
        {currentContractHistory.length ? (
          <div className="historySearch">
            <label>
              <i className="icon-history"></i>
            </label>
            <ul>
              {currentContractHistory.map((c) =>
                c.vehicle ? (
                  <li
                    key={c.id}
                    className={`history-item ${window.location.pathname.includes(c.id) ? `active` : ''}`}
                  >
                    <button className="clear-history" onClick={() => handleClearHistoryClick(c.id)}>
                      <i className="icon-close" style={{ fontSize: '8px' }}></i>
                    </button>

                    <NavLink to={`results/${c.id}`}>
                      <NumberPlate
                        licenseNumber={c.vehicle.licensePlate?.licenseNumber}
                        country={c.vehicle.licensePlate?.country}
                      />
                    </NavLink>
                  </li>
                ) : null
              )}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  )
}